import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "motion/react";
import { forwardRef, useMemo } from "react";
import { DismissButton, mergeProps, useDialog } from "react-aria";

import { isInIframe } from "../../utils/overlay";
import { mergeRefs } from "../../utils/refs";
import { Button } from "../button/Button";
import { useDrawerContext } from "./context";

import type { HTMLMotionProps, Variants } from "motion/react";
import type { HTMLAttributes, ReactNode, RefObject } from "react";
import type { AriaDialogProps } from "react-aria";

export interface DrawerContentProps
  extends AriaDialogProps,
    Omit<HTMLAttributes<HTMLDivElement>, "children" | "role"> {
  children: ReactNode | ((onClose: () => void) => ReactNode);
}

const ease = [0.36, 0.66, 0.4, 1] as const;

const getSlideVariants = (placement: "left" | "right" | "top" | "bottom"): Variants => {
  const transforms = {
    left: { enter: "translateX(0)", exit: "translateX(-100%)" },
    right: { enter: "translateX(0)", exit: "translateX(100%)" },
    top: { enter: "translateY(0)", exit: "translateY(-100%)" },
    bottom: { enter: "translateY(0)", exit: "translateY(100%)" },
  };

  return {
    enter: {
      transform: transforms[placement].enter,
      transition: {
        duration: 0.3,
        ease,
      },
    },
    exit: {
      transform: transforms[placement].exit,
      transition: {
        duration: 0.2,
        ease,
      },
    },
  };
};

const fadeVariants: Variants = {
  enter: {
    opacity: 1,
    transition: {
      duration: 0.3,
      ease,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
      ease,
    },
  },
};

export const DrawerContent = forwardRef<HTMLDivElement, DrawerContentProps>(
  ({ children, ...props }, ref) => {
    const {
      domRef,
      backdropProps,
      closeButtonProps,
      getDialogProps,
      hideCloseButton,
      slots,
      classNames,
      onClose,
      mask,
      placement,
    } = useDrawerContext();

    const { dialogProps } = useDialog({ role: "dialog" }, domRef);

    const inIframe = isInIframe();

    // In iframe contexts, skip dialogProps to avoid focus trapping and event capture
    // that would block parent window interactions
    const dialogPropsToApply = inIframe ? {} : dialogProps;

    const slideVariants = useMemo(() => getSlideVariants(placement), [placement]);
    const dialogPropsResult = getDialogProps(mergeProps(dialogPropsToApply, props));

    return (
      <div tabIndex={-1}>
        {/* Backdrop/Mask */}
        {mask && (
          <motion.div
            animate="enter"
            exit="exit"
            initial="exit"
            variants={fadeVariants}
            {...(backdropProps as HTMLMotionProps<"div">)}
          />
        )}
        {/* Drawer Panel Wrapper */}
        <div className={slots.wrapper({ className: classNames?.wrapper })} data-slot="wrapper">
          {/* Animated Container */}
          <motion.div
            animate="enter"
            exit="exit"
            initial="exit"
            variants={slideVariants}
            {...({
              className: dialogPropsResult.className,
              style: dialogPropsResult.style,
              "data-placement": placement,
            } as HTMLMotionProps<"div">)}
          >
            {/* Dialog Content */}
            <div
              ref={mergeRefs<HTMLDivElement | null>(
                ref,
                domRef as RefObject<HTMLDivElement | null>
              )}
              id={dialogPropsResult.id}
              data-open={dialogPropsResult["data-open"]}
              data-dismissable={dialogPropsResult["data-dismissable"]}
              aria-modal={dialogPropsResult["aria-modal"]}
              aria-labelledby={dialogPropsResult["aria-labelledby"]}
              aria-describedby={dialogPropsResult["aria-describedby"]}
              className="flex h-full flex-col"
            >
              <DismissButton onDismiss={onClose} />
              {!hideCloseButton && (
                <Button
                  {...closeButtonProps}
                  variant="light"
                  isIconOnly
                  size="sm"
                  aria-label="Close drawer"
                >
                  <FontAwesomeIcon icon={faXmark} />
                </Button>
              )}
              {typeof children === "function" ? children(onClose) : children}
            </div>
          </motion.div>
        </div>
      </div>
    );
  }
);
DrawerContent.displayName = "Drawer.Content";
